import { useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { apiGet, apiPost, apiPatch, apiDelete } from 'api';
import AuthContext from 'context/auth-context';

const getSystems = async (token) => {
  const url = 'admin/systems';
  const data = await apiGet({ url, token });
  return data;
};

const getSystem = async (token, id) => {
  const url = `admin/systems/${id}`;
  const data = await apiGet({ url, token });
  return data;
};

const createSystem = async (token, params) => {
  const url = 'admin/systems';
  const data = await apiPost({ url, token, objectName: 'system', params });
  return data;
};

const updateSystem = async (token, id, params) => {
  const url = `admin/systems/${id}`;
  const data = await apiPatch({ url, token, objectName: 'system', params });
  return data;
};

const deleteSystem = async (token, id) => {
  const url = `admin/systems/${id}`;
  const data = await apiDelete({ url, token });
  return data;
};

export function useAdminSystems({ options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getSystems(token);
  const queryKey = 'admin-systems';
  return useQuery(queryKey, queryFn, { ...options });
}

export function useAdminSystem({ id, options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getSystem(token, id);
  const queryKey = ['admin-system', id];
  return useQuery(queryKey, queryFn, { enabled: !!id, ...options });
}

export function useCreateAdminSystem({ options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => createSystem(token, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-systems');
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}

export function useUpdateAdminSystem({ id, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => updateSystem(token, id, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-systems');
        queryClient.invalidateQueries(['admin-system', id]);
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}

export function useDeleteAdminSystem({ options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (id) => deleteSystem(token, id),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-systems');
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}
